import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { formatCurrency } from '../../utils/formatters';
import { ArrowLeft, CreditCard, QrCode, FileText, CheckCircle, ShieldCheck } from 'lucide-react';
import { cn } from '../../utils/cn';

export function Checkout() {
    const location = useLocation();
    const navigate = useNavigate();


    // State from navigation
    const plan = location.state?.plan;
    const user = location.state?.user;
    const hasDiscount = location.state?.discount;

    const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'pix' | 'boleto'>('credit_card');
    const [coupon, setCoupon] = useState('');
    const [discountAmount, setDiscountAmount] = useState(hasDiscount ? 0.1 : 0); // 10% if from sales flow
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (!plan) {
            navigate('/');
        }
    }, [plan, navigate]);

    if (!plan) return null;

    const basePrice = plan.monthlyPrice; // Assuming monthly for now, could be passed from state
    const discountValue = basePrice * discountAmount;
    const total = basePrice - discountValue;

    const handleApplyCoupon = () => {
        if (coupon.toUpperCase() === 'BEMVINDO') {
            setDiscountAmount(0.15); // 15% example
        }
    };

    const handleFinish = () => {
        setIsProcessing(true);
        setTimeout(() => {
            setIsProcessing(false);
            alert('Compra realizada com sucesso! (Simulação)');
            navigate('/app');
        }, 2000);
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
            {/* Header Simplificado */}
            <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Voltar
                    </button>
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Pagamento 100% Seguro</span>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Finalizar Contratação</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Coluna da Esquerda: Pagamento */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Dados do Usuário (Review) */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Seus Dados</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="block text-gray-500 dark:text-gray-400">Nome</span>
                                    <span className="block font-medium dark:text-gray-200">{user?.name || '---'}</span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 dark:text-gray-400">Email</span>
                                    <span className="block font-medium dark:text-gray-200">{user?.email || '---'}</span>
                                </div>
                                <div>
                                    <span className="block text-gray-500 dark:text-gray-400">CNPJ</span>
                                    <span className="block font-medium dark:text-gray-200">{user?.cnpj || '---'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Pagamento */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Forma de Pagamento</h2>
                            </div>

                            {/* Tabs */}
                            <div className="flex border-b border-gray-200 dark:border-gray-700">
                                <button
                                    onClick={() => setPaymentMethod('credit_card')}
                                    className={cn(
                                        "flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2",
                                        paymentMethod === 'credit_card'
                                            ? "border-green-600 text-green-600 bg-green-50/50 dark:bg-green-900/10"
                                            : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    )}
                                >
                                    <CreditCard className="w-5 h-5" /> Cartão de Crédito
                                </button>
                                <button
                                    onClick={() => setPaymentMethod('pix')}
                                    className={cn(
                                        "flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2",
                                        paymentMethod === 'pix'
                                            ? "border-green-600 text-green-600 bg-green-50/50 dark:bg-green-900/10"
                                            : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    )}
                                >
                                    <QrCode className="w-5 h-5" /> Pix
                                </button>
                                <button
                                    onClick={() => setPaymentMethod('boleto')}
                                    className={cn(
                                        "flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2",
                                        paymentMethod === 'boleto'
                                            ? "border-green-600 text-green-600 bg-green-50/50 dark:bg-green-900/10"
                                            : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    )}
                                >
                                    <FileText className="w-5 h-5" /> Boleto
                                </button>
                            </div>

                            {/* Conteúdo Tabs */}
                            <div className="p-8">
                                {paymentMethod === 'credit_card' && (
                                    <div className="space-y-4 max-w-lg mx-auto animate-fade-in">
                                        <Input label="Número do Cartão" placeholder="0000 0000 0000 0000" />
                                        <div className="grid grid-cols-2 gap-4">
                                            <Input label="Validade" placeholder="MM/AA" />
                                            <Input label="CVC" placeholder="123" />
                                        </div>
                                        <Input label="Nome no Cartão" placeholder="Como impresso no cartão" />
                                    </div>
                                )}

                                {paymentMethod === 'pix' && (
                                    <div className="text-center py-8 animate-fade-in">
                                        <div className="w-48 h-48 bg-gray-200 mx-auto rounded-lg mb-4 flex items-center justify-center text-gray-500">
                                            QR Code Mock
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                            Escaneie o QR Code ou copie a chave abaixo para pagar.
                                            <br />Aprovação imediata.
                                        </p>
                                        <Button variant="secondary" size="sm">Copiar Chave Pix</Button>
                                    </div>
                                )}

                                {paymentMethod === 'boleto' && (
                                    <div className="text-center py-8 animate-fade-in">
                                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                                            O boleto será gerado após a confirmação.
                                            <br />Pode levar até 2 dias úteis para compensar.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Coluna da Direita: Resumo */}
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-200 dark:border-gray-700 sticky top-8">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6">Resumo do Pedido</h3>

                            <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
                                <div>
                                    <p className="font-semibold text-gray-900 dark:text-white">{plan.name}</p>
                                    <p className="text-sm text-gray-500">{plan.users} usuários</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-gray-900 dark:text-white">{formatCurrency(basePrice)}</p>
                                    <p className="text-xs text-gray-500">/mês</p>
                                </div>
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                                    <span>Subtotal</span>
                                    <span>{formatCurrency(basePrice)}</span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-sm text-green-600 font-medium">
                                        <span>Desconto ({discountAmount * 100}%)</span>
                                        <span>- {formatCurrency(discountValue)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-base font-bold text-gray-900 dark:text-white pt-3 border-t border-gray-100 dark:border-gray-700">
                                    <span>Total</span>
                                    <span>{formatCurrency(total)}</span>
                                </div>
                            </div>

                            {/* Cupom */}
                            <div className="mb-6">
                                <label className="block text-xs font-medium text-gray-500 mb-1">CUPOM DE DESCONTO</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={coupon}
                                        onChange={(e) => setCoupon(e.target.value)}
                                        placeholder="Código do cupom"
                                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                    <button
                                        onClick={handleApplyCoupon}
                                        className="px-3 py-2 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    >
                                        Aplicar
                                    </button>
                                </div>
                            </div>

                            <Button
                                onClick={handleFinish}
                                className="w-full text-lg h-14"
                                isLoading={isProcessing}
                            >
                                Confirmar Assinatura
                            </Button>

                            <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
                                <div className="mt-0.5"><CheckCircle className="w-3 h-3 text-green-500" /></div>
                                <p>Sua assinatura começa imediatamente após a confirmação do pagamento. Cancele a qualquer momento.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
